// src/views/Users/Customers/Modal/ActivityModal.jsx
import React, { useEffect, useState, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { modeConstants } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { activityActions } from "../../redux/actions";
import ActionsDataTable from "../DataTable/ActionsDataTable";

// --- UI/options -------------------------------------------------
const STAGES = [
  "Call",
  "ShortList",
  "Interview 1",
  "Interview 2",
  "Interview 3",
  "Interview 4",
  "Pending",
  "Accepted",
  "Rejected",
];

// --- Form defaults ----------------------------------------------
const DEFAULT_VALUES = {
  activityTitle: "",
  stage: "Call",
  projectId: "",
  candidateId: "",
  details: "",
};

export default function ActivityModal({ show, onHide, mode, isRecallProjectCandidates = true }) {
  const initialGridState = {
    length: 1,
    pageNumber: 1,
    orderBy: "",
    filterQuery: null,
    search: null,
  };

  const dispatch = useDispatch();
  const sortingInProgressRef = useRef(false);
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;
  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(initialGridState.pageNumber);
  const initialData = useSelector((s) => s.activityReducer.formData);
  const candidateData = useSelector((s) => s.candidateReducer.formData);
  const allProjectData = useSelector((s) => s.projectReducer.allData);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULT_VALUES });

  const [history, setHistory] = useState([]);

  const selectedCandidateId = watch("candidateId");
  const currentStage = watch("stage");
  const currentProjectId = watch("projectId");

  // Prefill when opening and apply candidateData lock
  useEffect(() => {
    if (!show) return;
    if (isAdd) {
      reset(DEFAULT_VALUES);
      setHistory([]);
    } else if (initialData && (isEdit || isView)) {
      const values = {
        activityTitle: initialData.title ?? "",
        stage: (initialData.status ?? "Call").toString(),
        projectId:
          initialData.projectId != null ? String(initialData.projectId) : "",
        candidateId:
          initialData.userId != null ? String(initialData.userId) : "",
        details: initialData.details ?? "",
      };

      reset(values);

      if (initialData.activityHistories) {
        setHistory(initialData.activityHistories || []);
      } else {
        setHistory([]);
      }
    } else {
      reset(DEFAULT_VALUES);
      setHistory([]);
    }
  }, [show, isAdd, isEdit, isView, initialData, reset, setValue]);
  const columns = [
    {
      name: "Sr. No",
      selector: (row, index) => index + 1, // start from 1
      sortable: false,
    },
    {
      id: "createdAt",
      name: "Date Time",
      selector: (r) => new Date(r?.createdAt * 1000).toLocaleString(),
      sortable: true,
      grow: 2,
    },
    {
      id: "status",
      name: "Stage",
      selector: (r) => r.status,
      sortable: true,
    },
    {
      id: "detail",
      name: "Remarks",
      selector: (r) => r.detail,
      sortable: true,
    },
  ];

  // sorting & pagination
  const onSort = (sortedColumn, sortDirection) => {
    sortingInProgressRef.current = true;
    const columnDirection = sortDirection
      ? sortDirection.toUpperCase() // "ASC" | "DESC"
      : null;
    setDefaultPage(gridState.pageNumber);
    setGridState((prevState) => ({
      ...prevState,
      orderBy:
        columnDirection !== null ? `${sortedColumn} ${columnDirection}` : "",
    }));
  };

  const onSubmit = (values) => {
    console.log("isRecallProjectCandidates", isRecallProjectCandidates);
    const payload = {
      Id: isEdit ? initialData?.id ?? 0 : 0,
      Title: values.activityTitle.trim(),
      Status: values.stage, // e.g., "Call", "ShortList", etc.
      ProjectId: values.projectId || candidateData?.project?.id, // null if not selected
      UserId: values.candidateId || candidateData.id,
      Details: values.details?.trim() ?? "",
      RecallId: isRecallProjectCandidates ? 0 : (values.projectId || candidateData?.project?.id),
      ActivityHistory: {
        Status: values.stage,
        Detail: values.details?.trim() ?? "",
      },
    };

    // Dispatch (switch to your real action names)
    if (isEdit) {
      dispatch(activityActions.updateActivity(payload, candidateData?.id, onHide));
    } else {
      dispatch(activityActions.createActivity(payload, candidateData?.id, onHide));
    }
  };

  const title = isAdd
    ? "Add Activity"
    : isEdit
    ? "Edit Activity"
    : "View Activity";

  const chipClass = (value) =>
    `btn btn-sm ${
      currentStage === value
        ? "btn-outline-success border-3"
        : "btn-outline-secondary"
    }`;

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isView}>
            <div className="row g-3">
              {/* Activity Title */}
              <div className="col-12">
                <div class="form-floating">
                  <input
                    id="title"
                    className="form-control"
                    placeholder="Add Activity Title"
                    {...register("activityTitle")}
                  />
                  <label for="title">Title</label>
                </div>
              </div>

              {/* Stage chips */}
              <div className="col-12">
                <div className="d-flex flex-wrap gap-3">
                  {STAGES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={chipClass(s)}
                      onClick={() =>
                        setValue("stage", s, { shouldDirty: true })
                      }
                      style={{
                        borderRadius: 12,
                        paddingInline: 18,
                        paddingBlock: 10,
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {/* keep an input bound for form value */}
                <input type="hidden" {...register("stage")} />
              </div>

              {/* Select Project & Candidate (controlled by RHF state) */}
              <div className="col-lg-6 pt-1">
                <div class="form-floating">
                   <input
                    id="project"
                    type="text"
                    value={candidateData?.project?.positionName}
                    className="form-control"
                    placeholder=""
                    disabled
                  />
                  <input
                    type="hidden"
                    className="form-control"
                    value={candidateData?.project?.id}
                    placeholder=""
                    {...register("projectId")}
                  />
                  {/* <select
                    id="project"
                    className="form-control"
                    {...register("projectId", {
                      required: "The field is required",
                    })}
                    value={candidateData?.project?.id}
                    onChange={(e) =>
                      setValue("projectId", e.target.value, {
                        shouldDirty: true,
                      })
                    }
                    disabled
                  >
                    <option value="">Select Project</option>
                    {allProjectData.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.positionName}
                      </option>
                    ))}
                  </select> */}
                  <label for="project">Project</label>
                </div>
                {errors.projectId && (
                  <small className="text-danger">
                    {errors.projectId.message}
                  </small>
                )}
              </div>

              <div className="col-lg-6 pt-1">
                <div class="form-floating">
                  <input
                    id="candidate"
                    type="text"
                    value={candidateData.name}
                    className="form-control"
                    placeholder=""
                    disabled
                  />
                  <input
                    type="hidden"
                    className="form-control"
                    value={candidateData.id}
                    placeholder=""
                    {...register("candidateId")}
                  />
                  <label for="candidate">Candidate Name</label>
                </div>
              </div>

              {/* Details */}
              <div className="col-12">
                <div class="form-floating">
                  <textarea
                    id="detail"
                    className="form-control"
                    rows={6}
                    placeholder="Detail Job Requirements"
                    {...register("details", {
                      required: "The field is required",
                    })}
                  />
                  <label for="detail">Job Description</label>
                </div>
                {errors.details && (
                  <small className="text-danger">
                    {errors.details.message}
                  </small>
                )}
              </div>

              {/* History Table */}
              <div className="col-12 mt-4">
                <h6>History :</h6>
                {history.length > 0 ? (
                  <ActionsDataTable
                    columns={columns}
                    filterColumns={[]}
                    data={history}
                    pagination={false}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    selectableRows
                    selectableRowsHighlight
                    selectableRowsVisibleOnly
                    paginationServer={false}
                    onSort={onSort}
                    gridState={gridState}
                    paginationTotalRows={history.length}
                    paginationDefaultPage={defaultPage}
                    paginationResetDefaultPage
                    allowToolbar={false}
                    allowSelection={false}
                    setGridState={setGridState}
                    sortServer={true}
                  />
                ) : (
                  <p className="text-muted">No history available.</p>
                )}
              </div>
            </div>
          </fieldset>

          <div className="d-flex justify-content-end gap-2 mt-5 footer-modal">
            <Button variant="outline-secondary" onClick={onHide} className="btn-footer">
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-footer">
                {isEdit ? "Save Changes" : "Create Activity"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
